import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTheme } from '../../context/ThemeContext';
import { 
  TrendingUp, TrendingDown, Calculator, Sliders, DollarSign, 
  MessageSquare, Calendar, Clock, Sparkles, RotateCcw,
  Target, Zap, Save, FolderOpen, Trash2, GitCompare, X, Check, ChevronDown
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  Legend
} from 'recharts';
import ExportButton from '../common/ExportButton';
import { exportToPDF, getTimestamp } from '../../utils/exportUtils';
import { formatDateAU } from '../../utils/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhatIfAnalyzer = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  const [scenario, setScenario] = useState({
    meetingReductionPercent: 0,
    messageReductionPercent: 0,
    revenueIncreasePercent: 0,
    handleTimeReductionPercent: 0,
  });

  // Saved scenarios state
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDesc, setSaveDesc] = useState('');
  const [saving, setSaving] = useState(false);

  // Compare state
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState(new Set());
  const [compareData, setCompareData] = useState(null);
  const [loadingCompare, setLoadingCompare] = useState(false);

  // Fetch saved scenarios
  const fetchSaved = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/scenarios`);
      setSavedScenarios(res.data.scenarios || []);
    } catch (e) {
      console.error('Failed to fetch scenarios:', e);
    }
  }, []);

  useEffect(() => { fetchSaved(); }, [fetchSaved]);

  // Debounced analysis
  const runAnalysis = useCallback(async () => {
    if (Object.values(scenario).every(v => v === 0)) {
      setResult(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/what-if-analysis`, scenario);
      setResult(response.data);
    } catch (err) {
      setError('Failed to run analysis');
    } finally {
      setLoading(false);
    }
  }, [scenario]);

  useEffect(() => {
    const timer = setTimeout(() => { runAnalysis(); }, 500);
    return () => clearTimeout(timer);
  }, [runAnalysis]);

  const handleSliderChange = (key, value) => {
    setScenario(prev => ({ ...prev, [key]: value }));
  };

  const resetScenario = () => {
    setScenario({ meetingReductionPercent: 0, messageReductionPercent: 0, revenueIncreasePercent: 0, handleTimeReductionPercent: 0 });
    setResult(null);
  };

  const presets = [
    { name: 'Efficiency Focus', icon: Zap, color: 'text-amber-500', values: { meetingReductionPercent: 25, messageReductionPercent: 20, handleTimeReductionPercent: 15, revenueIncreasePercent: 0 }},
    { name: 'Growth Focus', icon: TrendingUp, color: 'text-emerald-500', values: { meetingReductionPercent: 0, messageReductionPercent: 0, handleTimeReductionPercent: 0, revenueIncreasePercent: 15 }},
    { name: 'Balanced', icon: Target, color: 'text-blue-500', values: { meetingReductionPercent: 15, messageReductionPercent: 10, handleTimeReductionPercent: 10, revenueIncreasePercent: 10 }},
  ];

  const applyPreset = (preset) => { setScenario(preset.values); };

  // Save scenario
  const handleSave = async () => {
    if (!saveName.trim() || !result) return;
    setSaving(true);
    try {
      await axios.post(`${API}/scenarios`, {
        name: saveName.trim(),
        description: saveDesc.trim(),
        parameters: scenario,
        results: result
      });
      setShowSaveModal(false);
      setSaveName('');
      setSaveDesc('');
      fetchSaved();
    } catch (e) {
      console.error('Failed to save:', e);
    } finally {
      setSaving(false);
    }
  };

  // Load scenario
  const loadScenario = (s) => {
    setScenario(s.parameters);
    setShowSaved(false);
    setCompareMode(false);
    setCompareData(null);
  };

  // Delete scenario
  const deleteScenario = async (id) => {
    try {
      await axios.delete(`${API}/scenarios/${id}`);
      setSavedScenarios(prev => prev.filter(s => s.id !== id));
      setSelectedForCompare(prev => { const n = new Set(prev); n.delete(id); return n; });
    } catch (e) {
      console.error('Failed to delete:', e);
    }
  };

  // Toggle compare selection
  const toggleCompareSelect = (id) => {
    setSelectedForCompare(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); 
      else if (n.size < 5) n.add(id);
      return n;
    });
  };

  // Run comparison
  const runCompare = async () => {
    if (selectedForCompare.size < 2) return;
    setLoadingCompare(true);
    try {
      const res = await axios.post(`${API}/scenarios/compare`, { scenarioIds: [...selectedForCompare] });
      setCompareData(res.data.scenarios);
    } catch (e) {
      console.error('Compare failed:', e);
    } finally {
      setLoadingCompare(false);
    }
  };

  const handleExportPDF = async () => {
    await exportToPDF('what-if-content', `what_if_scenario_${getTimestamp()}.pdf`, {
      backgroundColor: isDark ? '#0a0a0b' : '#f8fafc'
    });
  };

  const comparisonData = result ? [
    { name: 'Revenue', baseline: result.baseline.totalRevenue, projected: result.projected.totalRevenue },
    { name: 'Workload', baseline: result.baseline.totalWorkload, projected: result.projected.totalWorkload },
    { name: 'Gross Margin', baseline: result.baseline.profit, projected: result.projected.profit },
  ] : [];

  const costBreakdownData = result ? [
    { name: 'Messages', baseline: result.baseline.messageCost, projected: result.projected.messageCost },
    { name: 'Handle Time', baseline: result.baseline.handleCost, projected: result.projected.handleCost },
    { name: 'Meetings', baseline: result.baseline.meetingCost, projected: result.projected.meetingCost },
  ] : [];

  const formatCurrency = (value) => {
    if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    return `$${(value / 1000).toFixed(0)}K`;
  };

  const COMPARE_COLORS = ['#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'];

  const SliderInput = ({ label, icon: Icon, value, onChange, max = 50, color = 'emerald' }) => (
    <div className={`p-4 rounded-xl border transition-colors duration-300
      ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-slate-50 border-slate-200'}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 text-${color}-500`} />
          <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{label}</span>
        </div>
        <span className={`text-lg font-bold text-${color}-500`}>{value}%</span>
      </div>
      <input
        type="range" min="0" max={max} value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className={`w-full h-2 rounded-lg appearance-none cursor-pointer
          ${isDark ? 'bg-white/10' : 'bg-slate-200'}
          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
          [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-${color}-500
          [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-pointer`}
      />
      <div className="flex justify-between mt-1">
        <span className={`text-xs ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>0%</span>
        <span className={`text-xs ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>{max}%</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" id="what-if-content" data-testid="what-if-analyzer">
      {/* Hero Section */}
      <div className={`relative overflow-hidden rounded-2xl p-8 border transition-colors duration-300
        ${isDark 
          ? 'bg-gradient-to-br from-purple-500/10 via-[#0d0d0f] to-emerald-500/10 border-white/5' 
          : 'bg-gradient-to-br from-purple-50 via-white to-emerald-50 border-slate-200'}`}>
        <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" />
        
        <div className="absolute top-8 right-8 z-10 flex items-center gap-2">
          <button
            data-testid="saved-scenarios-btn"
            onClick={() => { setShowSaved(!showSaved); setCompareMode(false); setCompareData(null); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-all
              ${showSaved
                ? 'bg-purple-500/10 border-purple-500/30 text-purple-600'
                : isDark ? 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
          >
            <FolderOpen className="w-4 h-4" />
            Saved ({savedScenarios.length})
          </button>
          <ExportButton 
            onExportPDF={handleExportPDF}
            showExcel={false}
            pdfLabel="Export Scenario"
          />
        </div>

        <div className="relative">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
              <Calculator className="w-6 h-6 text-purple-500" />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>What-If Scenario Analyzer</h1>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Simulate changes and see projected impact on profitability</p>
            </div>
          </div>
          
          {/* Preset Scenarios */}
          <div className="flex flex-wrap gap-3 mt-6">
            {presets.map((preset) => (
              <button
                key={preset.name}
                onClick={() => applyPreset(preset)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all duration-200
                  ${isDark 
                    ? 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20' 
                    : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300'}`}
              >
                <preset.icon className={`w-4 h-4 ${preset.color}`} />
                <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{preset.name}</span>
              </button>
            ))}
            <button onClick={resetScenario}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all duration-200
                ${isDark ? 'bg-red-500/10 border-red-500/20 hover:bg-red-500/20 text-red-400' : 'bg-red-50 border-red-200 hover:bg-red-100 text-red-600'}`}>
              <RotateCcw className="w-4 h-4" />
              <span className="text-sm font-medium">Reset</span>
            </button>
          </div>
        </div>
      </div>

      {/* Saved Scenarios Panel */}
      {showSaved && (
        <div data-testid="saved-scenarios-panel" className={`rounded-2xl border p-6 transition-colors duration-300
          ${isDark ? 'bg-[#0d0d0f]/50 border-white/5' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-purple-500" />
              <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Saved Scenarios</h2>
            </div>
            <div className="flex items-center gap-2">
              {!compareMode ? (
                <button
                  data-testid="enter-compare-mode-btn"
                  onClick={() => { setCompareMode(true); setSelectedForCompare(new Set()); setCompareData(null); }}
                  disabled={savedScenarios.length < 2}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all
                    ${savedScenarios.length < 2
                      ? 'opacity-40 cursor-not-allowed'
                      : isDark ? 'bg-blue-500/10 border border-blue-500/30 text-blue-400 hover:bg-blue-500/20' : 'bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100'
                    }`}
                >
                  <GitCompare className="w-3.5 h-3.5" /> Compare Scenarios
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    Select 2-5 scenarios
                  </span>
                  <button
                    data-testid="run-compare-btn"
                    onClick={runCompare}
                    disabled={selectedForCompare.size < 2 || loadingCompare}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all
                      ${selectedForCompare.size >= 2 ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'opacity-40 cursor-not-allowed bg-slate-200 text-slate-500'}`}
                  >
                    {loadingCompare ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    Compare ({selectedForCompare.size})
                  </button>
                  <button onClick={() => { setCompareMode(false); setSelectedForCompare(new Set()); setCompareData(null); }}
                    className="px-2 py-1.5 text-xs text-slate-400 hover:text-slate-300">
                    Cancel
                  </button>
                </div>
              )}
              <button onClick={() => setShowSaved(false)}
                className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-white/5 text-slate-500' : 'hover:bg-slate-100 text-slate-400'}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {savedScenarios.length === 0 ? (
            <div className={`p-8 text-center rounded-xl ${isDark ? 'bg-white/[0.02]' : 'bg-slate-50'}`}>
              <FolderOpen className={`w-10 h-10 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
              <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>No saved scenarios yet. Run an analysis and save it.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {savedScenarios.map((s) => {
                const isSelected = selectedForCompare.has(s.id);
                const profit = s.results?.impact?.profitChange || 0;
                const margin = s.results?.impact?.marginChange || 0;
                return (
                  <div key={s.id}
                    data-testid={`saved-scenario-card-${s.id}`}
                    className={`relative p-4 rounded-xl border transition-all cursor-pointer group
                      ${compareMode && isSelected
                        ? 'border-emerald-500/50 bg-emerald-500/5 ring-1 ring-emerald-500/20'
                        : isDark ? 'border-white/5 bg-white/[0.02] hover:border-white/10' : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                      }`}
                    onClick={() => compareMode ? toggleCompareSelect(s.id) : loadScenario(s)}
                  >
                    {compareMode && (
                      <div className={`absolute top-3 right-3 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all
                        ${isSelected ? 'bg-emerald-500 border-emerald-500' : isDark ? 'border-white/20' : 'border-slate-300'}`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                    )}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <h4 className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{s.name}</h4>
                        {s.description && <p className={`text-xs mt-0.5 truncate ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{s.description}</p>}
                      </div>
                      {!compareMode && (
                        <button onClick={(e) => { e.stopPropagation(); deleteScenario(s.id); }}
                          data-testid={`delete-scenario-${s.id}`}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${profit >= 0 ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}>
                        {profit >= 0 ? '+' : ''}{formatCurrency(profit)}
                      </span>
                      <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${margin >= 0 ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}>
                        {margin >= 0 ? '+' : ''}{margin.toFixed(1)}% margin
                      </span>
                    </div>
                    <div className={`flex gap-2 mt-2 text-[10px] ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                      {s.parameters.meetingReductionPercent > 0 && <span>Meet -{s.parameters.meetingReductionPercent}%</span>}
                      {s.parameters.messageReductionPercent > 0 && <span>Email -{s.parameters.messageReductionPercent}%</span>}
                      {s.parameters.handleTimeReductionPercent > 0 && <span>Handle -{s.parameters.handleTimeReductionPercent}%</span>}
                      {s.parameters.revenueIncreasePercent > 0 && <span>Rev +{s.parameters.revenueIncreasePercent}%</span>}
                    </div>
                    <p className={`text-[10px] mt-2 ${isDark ? 'text-slate-700' : 'text-slate-300'}`}>
                      {formatDateAU(s.createdAt)}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Compare Results */}
      {compareData && compareData.length >= 2 && (
        <div data-testid="compare-results" className={`rounded-2xl border p-6 transition-colors duration-300
          ${isDark ? 'bg-[#0d0d0f]/50 border-white/5' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <GitCompare className="w-5 h-5 text-blue-500" />
              <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Scenario Comparison</h2>
            </div>
            <button onClick={() => { setCompareData(null); setCompareMode(false); setSelectedForCompare(new Set()); }}
              className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-white/5 text-slate-500' : 'hover:bg-slate-100 text-slate-400'}`}>
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Comparison Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
                  <th className={`text-left py-3 pr-4 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Metric</th>
                  {compareData.map((s, i) => (
                    <th key={s.id} className="text-right py-3 px-3">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COMPARE_COLORS[i] }} />
                        <span className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{s.name}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-slate-100'}`}>
                {[
                  { label: 'Meetings', key: 'meetingReductionPercent', suffix: '%', prefix: '-' },
                  { label: 'Emails', key: 'messageReductionPercent', suffix: '%', prefix: '-' },
                  { label: 'Handle Time', key: 'handleTimeReductionPercent', suffix: '%', prefix: '-' },
                  { label: 'Revenue', key: 'revenueIncreasePercent', suffix: '%', prefix: '+' },
                ].map(row => (
                  <tr key={row.key}>
                    <td className={`py-2.5 pr-4 text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{row.label}</td>
                    {compareData.map(s => (
                      <td key={s.id} className={`py-2.5 px-3 text-right text-xs font-mono ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                        {s.parameters[row.key] > 0 ? `${row.prefix}${s.parameters[row.key]}${row.suffix}` : '-'}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className={`border-t-2 ${isDark ? 'border-white/10' : 'border-slate-300'}`}>
                  <td className={`py-3 pr-4 text-xs font-bold uppercase ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Gross Margin Change</td>
                  {compareData.map(s => {
                    const v = s.results?.impact?.profitChange || 0;
                    return (
                      <td key={s.id} className={`py-3 px-3 text-right text-sm font-bold ${v >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {v >= 0 ? '+' : ''}{formatCurrency(v)}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td className={`py-2.5 pr-4 text-xs font-bold uppercase ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Margin Change</td>
                  {compareData.map(s => {
                    const v = s.results?.impact?.marginChange || 0;
                    return (
                      <td key={s.id} className={`py-2.5 px-3 text-right text-sm font-bold ${v >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {v >= 0 ? '+' : ''}{v.toFixed(1)}%
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td className={`py-2.5 pr-4 text-xs font-bold uppercase ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Projected Margin</td>
                  {compareData.map(s => (
                    <td key={s.id} className={`py-2.5 px-3 text-right text-sm font-mono font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {(s.results?.projected?.margin || 0).toFixed(1)}%
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className={`py-2.5 pr-4 text-xs font-bold uppercase ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Projected Profit</td>
                  {compareData.map(s => (
                    <td key={s.id} className={`py-2.5 px-3 text-right text-sm font-mono font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {formatCurrency(s.results?.projected?.profit || 0)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className={`py-2.5 pr-4 text-xs font-bold uppercase ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Workload Savings</td>
                  {compareData.map(s => {
                    const v = s.results?.impact?.workloadChange || 0;
                    return (
                      <td key={s.id} className={`py-2.5 px-3 text-right text-sm font-bold ${v <= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {formatCurrency(Math.abs(v))}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Comparison Chart */}
          <div className="mt-6">
            <h3 className={`text-sm font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>Projected Profit Comparison</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={compareData.map((s, i) => ({
                  name: s.name.length > 15 ? s.name.substring(0, 15) + '...' : s.name,
                  profit: s.results?.projected?.profit || 0,
                  color: COMPARE_COLORS[i]
                }))} margin={{ top: 10, right: 20, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                  <XAxis dataKey="name" tick={{ fill: isDark ? '#64748b' : '#94a3b8', fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => formatCurrency(v)} tick={{ fill: isDark ? '#64748b' : '#94a3b8', fontSize: 11 }} />
                  <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ backgroundColor: isDark ? '#1a1a1f' : '#fff', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', borderRadius: '8px', color: isDark ? '#fff' : '#0f172a' }} />
                  <Bar dataKey="profit" name="Projected Profit" radius={[6, 6, 0, 0]}>
                    {compareData.map((entry, i) => (
                      <rect key={entry.name || i} fill={COMPARE_COLORS[i]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Scenario Controls */}
      <div className={`rounded-2xl border p-6 transition-colors duration-300
        ${isDark ? 'bg-[#0d0d0f]/50 border-white/5' : 'bg-white border-slate-200'}`}>
        <h2 className={`text-lg font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          <Sliders className="w-5 h-5 text-purple-500" />
          Scenario Parameters
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <SliderInput label="Reduce Meetings" icon={Calendar} value={scenario.meetingReductionPercent} onChange={(v) => handleSliderChange('meetingReductionPercent', v)} max={75} color="blue" />
          <SliderInput label="Reduce Emails" icon={MessageSquare} value={scenario.messageReductionPercent} onChange={(v) => handleSliderChange('messageReductionPercent', v)} max={75} color="amber" />
          <SliderInput label="Reduce Handle Time" icon={Clock} value={scenario.handleTimeReductionPercent} onChange={(v) => handleSliderChange('handleTimeReductionPercent', v)} max={50} color="purple" />
          <SliderInput label="Increase Revenue" icon={DollarSign} value={scenario.revenueIncreasePercent} onChange={(v) => handleSliderChange('revenueIncreasePercent', v)} max={50} color="emerald" />
        </div>
      </div>

      {/* Results */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
        </div>
      )}

      {error && (
        <div className={`p-6 rounded-xl border ${isDark ? 'bg-red-500/10 border-red-500/20 text-red-400' : 'bg-red-50 border-red-200 text-red-600'}`}>
          {error}
        </div>
      )}

      {result && !loading && (
        <>
          {/* Save Button */}
          <div className="flex justify-end">
            <button
              data-testid="save-scenario-btn"
              onClick={() => setShowSaveModal(true)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all
                ${isDark ? 'bg-purple-500/20 border border-purple-500/30 text-purple-300 hover:bg-purple-500/30' : 'bg-purple-50 border border-purple-200 text-purple-700 hover:bg-purple-100'}`}
            >
              <Save className="w-4 h-4" /> Save This Scenario
            </button>
          </div>

          {/* Impact Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className={`p-5 rounded-xl border transition-colors duration-300 ${isDark ? 'bg-[#0d0d0f]/50 border-white/5' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Gross Margin Change</span>
                {result.impact.profitChange >= 0 ? <TrendingUp className="w-4 h-4 text-emerald-500" /> : <TrendingDown className="w-4 h-4 text-red-500" />}
              </div>
              <p className={`text-2xl font-bold ${result.impact.profitChange >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                {result.impact.profitChange >= 0 ? '+' : ''}{formatCurrency(result.impact.profitChange)}
              </p>
              <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                {result.impact.profitChangePercent >= 0 ? '+' : ''}{result.impact.profitChangePercent.toFixed(1)}% change
              </p>
            </div>
            <div className={`p-5 rounded-xl border transition-colors duration-300 ${isDark ? 'bg-[#0d0d0f]/50 border-white/5' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Margin Change</span>
                <Sparkles className={`w-4 h-4 ${result.impact.marginChange >= 0 ? 'text-emerald-500' : 'text-red-500'}`} />
              </div>
              <p className={`text-2xl font-bold ${result.impact.marginChange >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                {result.impact.marginChange >= 0 ? '+' : ''}{result.impact.marginChange.toFixed(1)}%
              </p>
              <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                From {result.baseline.margin.toFixed(1)}% to {result.projected.margin.toFixed(1)}%
              </p>
            </div>
            <div className={`p-5 rounded-xl border transition-colors duration-300 ${isDark ? 'bg-[#0d0d0f]/50 border-white/5' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Workload Savings</span>
                <DollarSign className="w-4 h-4 text-blue-500" />
              </div>
              <p className={`text-2xl font-bold ${result.impact.workloadChange <= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                {result.impact.workloadChange <= 0 ? '' : '+'}{formatCurrency(result.impact.workloadChange)}
              </p>
              <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                {result.impact.workloadChangePercent.toFixed(1)}% change
              </p>
            </div>
            <div className={`p-5 rounded-xl border transition-colors duration-300 ${isDark ? 'bg-[#0d0d0f]/50 border-white/5' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Per Client Impact</span>
                <Target className="w-4 h-4 text-purple-500" />
              </div>
              <p className={`text-2xl font-bold ${result.impact.perClientProfitChange >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                {result.impact.perClientProfitChange >= 0 ? '+' : ''}${result.impact.perClientProfitChange.toFixed(0)}
              </p>
              <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Per client annually</p>
            </div>
          </div>

          {/* Summary Banner */}
          <div className={`p-6 rounded-xl border transition-colors duration-300
            ${result.impact.profitChange >= 0 
              ? isDark ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-emerald-50 border-emerald-200'
              : isDark ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-center gap-3">
              {result.impact.profitChange >= 0 ? <TrendingUp className="w-6 h-6 text-emerald-500" /> : <TrendingDown className="w-6 h-6 text-red-500" />}
              <p className={`text-lg font-medium ${result.impact.profitChange >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {result.summary}
              </p>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className={`rounded-2xl border p-6 transition-colors duration-300 ${isDark ? 'bg-[#0d0d0f]/50 border-white/5' : 'bg-white border-slate-200'}`}>
              <h3 className={`text-sm font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Baseline vs Projected</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={comparisonData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                    <XAxis dataKey="name" tick={{ fill: isDark ? '#64748b' : '#94a3b8', fontSize: 12 }} />
                    <YAxis tickFormatter={(v) => `$${(v/1000000).toFixed(1)}M`} tick={{ fill: isDark ? '#64748b' : '#94a3b8', fontSize: 11 }} />
                    <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: isDark ? '#1a1a1f' : '#fff', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', borderRadius: '8px', color: isDark ? '#fff' : '#0f172a' }} />
                    <Legend />
                    <Bar dataKey="baseline" name="Current" fill={isDark ? '#64748b' : '#94a3b8'} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="projected" name="Projected" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className={`rounded-2xl border p-6 transition-colors duration-300 ${isDark ? 'bg-[#0d0d0f]/50 border-white/5' : 'bg-white border-slate-200'}`}>
              <h3 className={`text-sm font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Cost Breakdown</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={costBreakdownData} layout="vertical" margin={{ top: 20, right: 30, left: 80, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} horizontal={false} />
                    <XAxis type="number" tickFormatter={(v) => `$${(v/1000000).toFixed(1)}M`} tick={{ fill: isDark ? '#64748b' : '#94a3b8', fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" tick={{ fill: isDark ? '#64748b' : '#94a3b8', fontSize: 12 }} />
                    <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: isDark ? '#1a1a1f' : '#fff', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', borderRadius: '8px', color: isDark ? '#fff' : '#0f172a' }} />
                    <Legend />
                    <Bar dataKey="baseline" name="Current" fill="#ef4444" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="projected" name="Projected" fill="#10b981" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Impact Areas */}
          {result.impactAreas.length > 0 && (
            <div className={`rounded-2xl border p-6 transition-colors duration-300 ${isDark ? 'bg-[#0d0d0f]/50 border-white/5' : 'bg-white border-slate-200'}`}>
              <h3 className={`text-sm font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Impact Breakdown</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {result.impactAreas.map((area) => (
                  <div key={area.area} className={`p-4 rounded-xl border transition-colors duration-300 ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                    <span className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{area.area}</span>
                    <p className={`text-sm mt-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{area.insight}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {!result && !loading && (
        <div className={`p-12 rounded-xl border text-center transition-colors duration-300 ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-slate-50 border-slate-200'}`}>
          <Calculator className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
          <p className={`text-lg font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Adjust the sliders above to see projected impact</p>
          <p className={`text-sm mt-2 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>Or select a preset scenario to get started</p>
        </div>
      )}

      {/* Save Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowSaveModal(false)}>
          <div
            data-testid="save-scenario-modal"
            onClick={e => e.stopPropagation()}
            className={`w-full max-w-md rounded-2xl border p-6 shadow-2xl
              ${isDark ? 'bg-[#1a1a1d] border-white/10' : 'bg-white border-slate-200'}`}
          >
            <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Save className="w-5 h-5 text-purple-500" /> Save Scenario
            </h3>
            <div className="space-y-3">
              <div>
                <label className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Name *</label>
                <input
                  data-testid="save-scenario-name-input"
                  type="text"
                  value={saveName}
                  onChange={e => setSaveName(e.target.value)}
                  placeholder="e.g., Q2 Efficiency Plan"
                  className={`w-full mt-1 px-3 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-1 focus:ring-purple-500/30
                    ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-slate-500' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'}`}
                  autoFocus
                />
              </div>
              <div>
                <label className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Description (optional)</label>
                <input
                  data-testid="save-scenario-desc-input"
                  type="text"
                  value={saveDesc}
                  onChange={e => setSaveDesc(e.target.value)}
                  placeholder="Brief note about this scenario"
                  className={`w-full mt-1 px-3 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-1 focus:ring-purple-500/30
                    ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-slate-500' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'}`}
                />
              </div>
              <div className={`p-3 rounded-xl text-xs ${isDark ? 'bg-white/[0.02] text-slate-500' : 'bg-slate-50 text-slate-400'}`}>
                Meet: -{scenario.meetingReductionPercent}% | Email: -{scenario.messageReductionPercent}% | Handle: -{scenario.handleTimeReductionPercent}% | Rev: +{scenario.revenueIncreasePercent}%
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setShowSaveModal(false)}
                className={`px-4 py-2 rounded-xl text-sm font-medium ${isDark ? 'text-slate-400 hover:bg-white/5' : 'text-slate-500 hover:bg-slate-100'}`}>
                Cancel
              </button>
              <button
                data-testid="save-scenario-confirm-btn"
                onClick={handleSave}
                disabled={!saveName.trim() || saving}
                className={`flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold transition-all
                  ${saveName.trim() ? 'bg-purple-500 text-white hover:bg-purple-600' : 'opacity-40 cursor-not-allowed bg-purple-500/50 text-white/50'}`}
              >
                {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatIfAnalyzer;
